import { FormEvent, useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { config } from '../config/environment'
import { clearSignedOut } from '../hooks/useAuth'
import { SERIF_HEADING } from '../theme'

interface EmailCodeLoginProps {
  message?: string | null
}

export function EmailCodeLogin({ message }: EmailCodeLoginProps) {
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [codeRequested, setCodeRequested] = useState(false)
  const [working, setWorking] = useState(false)
  const [requestError, setRequestError] = useState<string | null>(null)

  useEffect(() => {
    if (!new URLSearchParams(window.location.search).has('auth_error')) return
    const clean = new URL(window.location.href)
    clean.searchParams.delete('auth_error')
    clean.searchParams.delete('error')
    clean.searchParams.delete('error_description')
    window.history.replaceState({}, '', clean.toString())
  }, [])

  const requestCode = async (event: FormEvent) => {
    event.preventDefault()
    setWorking(true)
    setRequestError(null)
    try {
      const response = await fetch(`${config.API_CF_URL}/auth/email-code`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      if (!response.ok) throw new Error(`request ${response.status}`)
      // Logout is deliberately sticky across reloads. Beginning a new login must
      // release that latch so the verified session can resume on the next load.
      clearSignedOut()
      setCode('')
      setCodeRequested(true)
    } catch {
      setRequestError('We could not send the code right now. Please try again in a moment.')
    } finally {
      setWorking(false)
    }
  }

  const verifyCode = async (event: FormEvent) => {
    event.preventDefault()
    setWorking(true)
    setRequestError(null)
    try {
      const response = await fetch(`${config.API_CF_URL}/auth/email-code/verify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      })
      if (!response.ok) {
        setRequestError(response.status === 429
          ? 'Too many attempts. Wait a minute, then request a new code.'
          : 'That code is invalid or has expired. Check the code or request a new one.')
        return
      }
      clearSignedOut()
      window.location.assign(window.location.pathname + window.location.search + window.location.hash)
    } catch {
      setRequestError('We could not verify the code right now. Please try again in a moment.')
    } finally {
      setWorking(false)
    }
  }

  const personalEnvironment = config.ENVIRONMENT.startsWith('dev-')

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: 'background.default', p: 2 }}>
      <Box
        component="main"
        sx={{
          width: '100%', maxWidth: 410, textAlign: 'center',
          px: { xs: 3.5, sm: 4.5 }, py: 5,
          bgcolor: 'background.paper',
          border: '1px solid', borderColor: 'divider', borderRadius: 4,
          boxShadow: '0 1px 3px rgba(40,36,31,0.04), 0 8px 30px rgba(40,36,31,0.06)',
        }}
      >
        <Box
          sx={{
            width: 52, height: 52, mx: 'auto', mb: 2.5, borderRadius: 3.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: theme => `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
            color: 'primary.contrastText',
          }}
          aria-hidden
        >
          <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </Box>

        <Typography variant="h5" sx={{ ...SERIF_HEADING, color: 'text.primary', mb: 1 }}>
          Welcome to Ros
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', lineHeight: 1.5, mb: 3 }}>
          Sign in to your research workspace
        </Typography>

        {message && !codeRequested && <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>{message}</Alert>}
        {requestError && <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>{requestError}</Alert>}

        {codeRequested ? (
          <Box component="form" onSubmit={verifyCode}>
            <Typography sx={{ fontWeight: 650, mb: 1 }}>Enter your code</Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.88rem', lineHeight: 1.55, mb: 3 }}>
              If {email} has access, a six-digit code is on its way. It expires in 10 minutes.
            </Typography>
            <TextField
              type="text"
              label="Six-digit code"
              placeholder="123456"
              value={code}
              onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
              autoComplete="one-time-code"
              autoFocus
              required
              fullWidth
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
              sx={{ mb: 2 }}
            />
            <Button type="submit" variant="contained" fullWidth disabled={working || code.length !== 6} sx={{ py: 1.25, fontWeight: 600, textTransform: 'none' }}>
              {working ? 'Verifying…' : 'Verify and sign in'}
            </Button>
            <Button type="button" variant="text" fullWidth disabled={working} onClick={() => {
              setCodeRequested(false)
              setCode('')
              setRequestError(null)
            }} sx={{ mt: 1, py: 1.15, textTransform: 'none' }}>
              Use another email
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={requestCode}>
            <TextField
              type="email"
              label="Email address"
              placeholder="name@company.com"
              value={email}
              onChange={event => setEmail(event.target.value)}
              autoComplete="email"
              autoFocus
              required
              fullWidth
              sx={{ mb: 2 }}
            />
            <Button type="submit" variant="contained" fullWidth disabled={working} sx={{ py: 1.25, fontWeight: 600, textTransform: 'none' }}>
              {working ? 'Sending…' : 'Email me a sign-in code'}
            </Button>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.76rem', lineHeight: 1.5, mt: 2 }}>
              Access is currently limited to invited users.
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 4, pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
          {personalEnvironment && (
            <Typography sx={{ color: 'text.secondary', fontSize: '0.72rem', mb: 0.5 }}>
              Personal environment · {config.ENVIRONMENT}
            </Typography>
          )}
          <Typography sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
            Ros Next · Experimental
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}
